<template>
  <div class="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200">
    <div class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-space-y-0 tw-pb-2 tw-p-6">
      <div>
        <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
          <TrendingUp class="tw-w-5 tw-h-5 tw-text-gray-600" />
          <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">
            {{ t('customer_management.charts.average_spending') }}
          </h3>
        </div>
      </div>
      <div class="tw-w-64">
        <DateRangePicker
          v-model="dateRange"
          :placeholder="t('common.datepicker.select_date_range')"
          :show-clear-button="false"
        />
      </div>
    </div>

    <div class="tw-px-6 tw-pb-6">
      <div class="tw-mb-4">
        <div class="tw-text-2xl tw-font-bold tw-text-gray-900">
          {{ formatCurrency(totalSpent) }}
        </div>
        <p class="tw-text-sm tw-text-gray-600">
          {{ t('customer_management.charts.total_spent') }}
        </p>
      </div>

      <div class="tw-h-[300px] tw-w-full">
        <InocuAreaChart
          :series="chartSeries"
          :xaxis="chartCategories"
          :is-money="true"
          :is-loading="isLoading"
          dark
        >
          <template #title>
            <span class="tw-sr-only">{{ t('customer_management.charts.average_spending') }}</span>
          </template>
        </InocuAreaChart>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { TrendingUp } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useFormatValue } from '@/_helpers/composables/formatting.composable';
import { useDateRange } from '@/_helpers/composables/date-range.composable';
import { useCustomerStore } from '@/stores/customer.store';
import moment from 'moment';
import InocuAreaChart from '@/components/shared/charts/InocuAreaChart.vue';
import { DateRangePicker } from '@/components/ui/date-picker';

interface Props {
  customerId: number;
}

const props = defineProps<Props>();
const { t } = useI18n();
const { formatCurrency } = useFormatValue();
const { thisMonthDates } = useDateRange();
const { getAverageSpendingChart } = useCustomerStore();

const isLoading = ref(false);
const spendingData = ref<Array<{ month: string; spending: number }>>([]);

// Date range state for the picker
const dateRange = ref<{
  from: Date | undefined;
  to: Date | undefined;
}>({
  from: undefined,
  to: undefined,
});

const getChartData = async (date: any) => {
  try {
    isLoading.value = true;
    let startDate = date.startDate;
    let endDate = date.endDate;

    if (new Date(endDate) > new Date()) {
      endDate = new Date();
      endDate = moment(endDate).format('YYYY-MM-DD');
    }

    let chartData: any = await getAverageSpendingChart(startDate, endDate, props.customerId);

    // Transform API data to component format
    const newData: Array<{ month: string; spending: number }> = [];

    if (chartData['X-axis'] && chartData['Y-axis']) {
      chartData['X-axis'].forEach((month: any, index: number) => {
        newData.push({
          month: month,
          spending: chartData['Y-axis'][index] || 0,
        });
      });
    }

    spendingData.value = newData;
  } catch (error) {
    console.error('Error fetching chart data:', error);
    spendingData.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Watch for date range changes
watch(
  dateRange,
  newDateRange => {
    if (newDateRange.from && newDateRange.to) {
      getChartData({
        startDate: moment(newDateRange.from).format('YYYY-MM-DD'),
        endDate: moment(newDateRange.to).format('YYYY-MM-DD'),
      });
    }
  },
  { deep: true }
);

const totalSpent = computed(() => {
  if (spendingData.value.length === 0) return 0;
  return spendingData.value.reduce((acc, curr) => acc + curr.spending, 0);
});

const chartSeries = computed(() => [
  {
    name: 'Spending',
    data: spendingData.value.map(item => item.spending),
  },
]);

const chartCategories = computed(() => {
  return spendingData.value.map(item => item.month);
});

// No need to expose getChartData anymore as it's handled internally

onMounted(async () => {
  // Initialize with current month dates
  const { startDate, endDate } = thisMonthDates;
  dateRange.value = {
    from: new Date(startDate),
    to: new Date(endDate),
  };

  await getChartData(thisMonthDates);
});
</script>
